// EN combo route: /en/services/$service/$location
// English mirror of TH combo pages with LocalBusiness + Service + FAQ + Breadcrumb JSON-LD.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  Phone,
  MessageCircle,
  Mail,
  CheckCircle2,
  Clock,
  Globe2,
  MapPin,
  Star,
  ArrowRight,
} from "lucide-react";
import type { ServiceId } from "@/data/services";
import { ComboServicePageEn } from "@/components/ComboServicePageEn";
import { getLocation, locations } from "@/data/locations";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { consolidateGraph } from "@/lib/schema-graph";
import { comboTitleEn, cityEn, serviceKeywordEn } from "@/lib/seo-keywords";
import { getReviewerForService, attorneyPersonSchema } from "@/data/service-reviewers";
import { RelatedComparisons, categoryForService } from "@/components/RelatedComparisons";
import {
  RelatedServicesInLocation,
  type ServiceKind,
} from "@/components/RelatedServicesInLocation";
import { EntityPanel } from "@/components/EntityPanel";

const SITE = "https://nyclanguageinstitute.com";


export const Route = createFileRoute("/en/services/$service/$location")({
  loader: async ({ params }) => {
    const loc = getLocation(params.location);
    if (!loc) throw notFound();
    const [{ SERVICE_EXTRA }, { SERVICE_HEAD, resolveServiceIdLight }] = await Promise.all([
      import("@/data/services-extra.gen"),
      import("@/data/services-head.gen"),
    ]);
    const id = resolveServiceIdLight(params.service) as ServiceId | undefined;
    if (!id) throw notFound();
    const extra = SERVICE_EXTRA[id];
    return {
      service: id,
      locationSlug: params.location,
      extra,
      svcMeta: { ...SERVICE_HEAD[id], faqs: extra?.faqs ?? [] },
    };
  },
  head: ({ params, loaderData }) => {
    const loc = getLocation(params.location);
    if (!loc || !loaderData)
      return {
        meta: [{ title: ctaTitle("Location not found", "/en/services/$service/$location", "en") }],
      };
    const s = loaderData.svcMeta;
    const enUrl = `${SITE}/en/services/${s.slug}/${loc.slug}`;
    const thUrl = `${SITE}/services/${s.slug}/${loc.slug}`;
    const city = cityEn(loc);
    const kw = serviceKeywordEn(loaderData.service, s.nameEn);
    const title = comboTitleEn({
      serviceId: loaderData.service,
      serviceNameEn: s.nameEn,
      loc,
      priceFrom: s.priceFrom,
    });
    const desc = `${kw} in ${city} (${loc.nameEn}). Certified · ${s.turnaround} · Pickup & delivery · MFA · NAATI · Reply in 15 min. Message us for a same-day answer.`;

    const jsonLdLocalBusiness = {
      "@context": "https://schema.org",
      "@type": "LegalService",
      "@id": `${enUrl}#localbusiness`,
      name: `NYC Legal & Notary Service — ${loc.nameEn}`,
      alternateName: "NYC Legal",
      description: desc,
      url: enUrl,
      inLanguage: "en",
      telephone: "+66-83-249-4999",
      priceRange: "฿฿",
      areaServed: {
        "@type": "Place",
        name: loc.nameEn,
        ...(loc.lat && loc.lng
          ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
          : {}),
      },
      address: {
        "@type": "PostalAddress",
        streetAddress: "61 Soi Lat Phrao 95 (Prangthip), Wang Thonglang",
        addressLocality: "Bangkok",
        postalCode: "10310",
        addressCountry: "TH",
      },
      openingHours: "Mo-Sa 09:00-18:00",
      makesOffer: {
        "@type": "Offer",
        priceCurrency: "THB",
        price: s.priceFrom,
        itemOffered: { "@type": "Service", name: s.nameEn },
      },
      sameAs: [thUrl],
    };

    const reviewer = getReviewerForService(loaderData.service);
    const jsonLdService = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${s.nameEn} — ${loc.nameEn}`,
      serviceType: s.nameEn,
      description: s.taglineEn,
      provider: { "@id": `${enUrl}#localbusiness` },
      areaServed: { "@type": "Place", name: loc.nameEn },
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "THB",
        lowPrice: s.priceFrom,
        highPrice: s.priceTo,
        availability: "https://schema.org/InStock",
      },
      reviewedBy: attorneyPersonSchema(reviewer, SITE),
    };

    const jsonLdFaq = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: s.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };

    const jsonLdBreadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "EN Home", item: `${SITE}/en` },
        { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/en/services` },
        { "@type": "ListItem", position: 3, name: s.nameEn, item: `${SITE}/en/services/${s.slug}` },
        { "@type": "ListItem", position: 4, name: loc.nameEn, item: enUrl },
      ],
    };

    // Round 11 — jsonLdClaimReview removed (Google policy: ClaimReview is
    // reserved for IFCN-signatory fact-checking orgs; marketing pages must
    // not emit it).
    const jsonLdClaimReview = null;

    return {
      meta: [
        { title: ctaTitle(title, "/en/services/$service/$location", "en") },
        {
          name: "description",
          content: ctaDesc(desc.slice(0, 158), "/en/services/$service/$location", "en"),
        },
        {
          name: "keywords",
          content: [...s.keywords, loc.nameEn, `${s.nameEn} ${loc.nameEn}`].join(", "),
        },
        { property: "og:title", content: ctaTitle(title, "/en/services/$service/$location", "en") },
        {
          property: "og:description",
          content: ctaDesc(desc.slice(0, 158), "/en/services/$service/$location", "en"),
        },
        { property: "og:url", content: enUrl },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "th_TH" },
        { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: enUrl },
        ...hreflangLinks(`/services/${s.slug}/${loc.slug}`, `/en/services/${s.slug}/${loc.slug}`),
      ],
      scripts: [
        consolidateGraph([
          { type: "application/ld+json", children: JSON.stringify(jsonLdLocalBusiness) },
          { type: "application/ld+json", children: JSON.stringify(jsonLdService) },
          { type: "application/ld+json", children: JSON.stringify(jsonLdFaq) },
          { type: "application/ld+json", children: JSON.stringify(jsonLdBreadcrumb) },
          ...(jsonLdClaimReview
            ? [
                {
                  type: "application/ld+json" as const,
                  children: JSON.stringify(jsonLdClaimReview),
                },
              ]
            : []),
        ]),
      ],
    };
  },
  component: EnComboPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <Link to="/en/services" className="mt-4 inline-block text-amber-300 hover:underline">
        ← Back to services
      </Link>
    </main>
  ),
});

function EnComboPage() {
  const { service, locationSlug } = Route.useLoaderData() as {
    service: ServiceId;
    locationSlug: string;
  };
  return <ComboServicePageEn service={service} locationSlug={locationSlug} />;
}
